const MAX_SAMPLES = 140_000;
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
const severity = (score) => score >= 0.65 ? 'high' : score >= 0.35 ? 'medium' : 'low';
export class VideoArtifactsAnalyzer {
    id = 'video-artifacts';
    label = 'Video Artifacts';
    analyzeEveryNFrames;
    previous;
    previousIntervalMs;
    lastResult;
    constructor(options = {}) {
        this.analyzeEveryNFrames = Math.max(1, Math.floor(options.analyzeEveryNFrames ?? 1));
    }
    reset() {
        this.previous = undefined;
        this.previousIntervalMs = undefined;
        this.lastResult = undefined;
    }
    analyze(frame, context) {
        const features = this.extractFeatures(frame, context.timestampMs);
        const previous = this.previous;
        const intervalMs = previous ? context.timestampMs - previous.timestampMs : undefined;
        if (this.lastResult && context.frameNumber % this.analyzeEveryNFrames !== 0) {
            this.previous = features;
            if (intervalMs !== undefined && intervalMs > 0)
                this.previousIntervalMs = intervalMs;
            return {
                ...this.lastResult,
                frame_number: context.frameNumber,
                timestamp_ms: context.timestampMs,
                warnings: this.lastResult.warnings.map((warning) => ({ ...warning, frame_number: context.frameNumber })),
            };
        }
        const { width, height, luma, cb, cr } = features;
        const total = Math.max(1, width * height);
        const histogram = new Array(256).fill(0);
        let minLuma = 255;
        let maxLuma = 0;
        let boundaryDifference = 0;
        let boundaryCount = 0;
        let interiorDifference = 0;
        let interiorCount = 0;
        let lumaResidual = 0;
        let chromaResidual = 0;
        let residualCount = 0;
        let edgeCount = 0;
        let strongEdgeCount = 0;
        let haloCount = 0;
        let alternationCount = 0;
        let alternationSamples = 0;
        let adjacentRowDifference = 0;
        let adjacentRowCount = 0;
        let parityRowDifference = 0;
        let parityRowCount = 0;
        const deadPixels = [];
        const artifactCells = new Array(12 * 8).fill(0);
        const artifactCellCounts = new Array(12 * 8).fill(0);
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const index = y * width + x;
                const value = luma[index] ?? 0;
                const bin = Math.max(0, Math.min(255, Math.round(value)));
                histogram[bin] = (histogram[bin] ?? 0) + 1;
                minLuma = Math.min(minLuma, value);
                maxLuma = Math.max(maxLuma, value);
                if (x > 0) {
                    const difference = Math.abs(value - (luma[index - 1] ?? value));
                    if (x % 8 === 0) {
                        boundaryDifference += difference;
                        boundaryCount++;
                    }
                    else {
                        interiorDifference += difference;
                        interiorCount++;
                    }
                }
                if (y > 0) {
                    const difference = Math.abs(value - (luma[index - width] ?? value));
                    if (y % 8 === 0) {
                        boundaryDifference += difference;
                        boundaryCount++;
                    }
                    else {
                        interiorDifference += difference;
                        interiorCount++;
                    }
                    adjacentRowDifference += difference;
                    adjacentRowCount++;
                }
                if (y > 1) {
                    parityRowDifference += Math.abs(value - (luma[index - width * 2] ?? value));
                    parityRowCount++;
                }
                if (x <= 0 || x >= width - 1 || y <= 0 || y >= height - 1)
                    continue;
                const left = luma[index - 1] ?? value;
                const right = luma[index + 1] ?? value;
                const up = luma[index - width] ?? value;
                const down = luma[index + width] ?? value;
                const localMean = (left + right + up + down) / 4;
                const residual = Math.abs(value - localMean);
                lumaResidual += residual;
                chromaResidual += Math.hypot((cb[index] ?? 0) - ((cb[index - 1] ?? 0) + (cb[index + 1] ?? 0) + (cb[index - width] ?? 0) + (cb[index + width] ?? 0)) / 4, (cr[index] ?? 0) - ((cr[index - 1] ?? 0) + (cr[index + 1] ?? 0) + (cr[index - width] ?? 0) + (cr[index + width] ?? 0)) / 4);
                residualCount++;
                const column = Math.min(11, Math.floor((x / width) * 12));
                const row = Math.min(7, Math.floor((y / height) * 8));
                const cellIndex = row * 12 + column;
                artifactCells[cellIndex] = (artifactCells[cellIndex] ?? 0) + residual;
                artifactCellCounts[cellIndex] = (artifactCellCounts[cellIndex] ?? 0) + 1;
                const gradient = Math.hypot(right - left, down - up);
                if (gradient > 20)
                    edgeCount++;
                if (gradient > 80)
                    strongEdgeCount++;
                const localMinimum = value < Math.min(left, right) - 18 || value < Math.min(up, down) - 18;
                const localMaximum = value > Math.max(left, right) + 18 || value > Math.max(up, down) + 18;
                if (gradient > 70 && (localMinimum || localMaximum))
                    haloCount++;
                if (x > 1) {
                    const gradientA = left - (luma[index - 2] ?? left);
                    const gradientB = value - left;
                    alternationSamples++;
                    if (Math.abs(gradientA) > 25 && Math.abs(gradientB) > 25 && Math.sign(gradientA) !== Math.sign(gradientB))
                        alternationCount++;
                }
                if (deadPixels.length < 100) {
                    const neighbors = [left, right, up, down, luma[index - width - 1], luma[index - width + 1], luma[index + width - 1], luma[index + width + 1]];
                    const neighborMean = neighbors.reduce((sum, item) => sum + item, 0) / neighbors.length;
                    const neighborSpread = Math.max(...neighbors) - Math.min(...neighbors);
                    if (Math.abs(value - neighborMean) > 100 && neighborSpread < 28)
                        deadPixels.push({ x, y });
                }
            }
        }
        const boundaryMean = boundaryDifference / Math.max(1, boundaryCount);
        const interiorMean = interiorDifference / Math.max(1, interiorCount);
        const macroblockingScore = clamp01((boundaryMean - interiorMean - 2) / 28);
        const occupiedBins = histogram.filter((count) => count > 0).length;
        const bandingScore = maxLuma - minLuma > 25 ? clamp01((32 - occupiedBins) / 28) : 0;
        const meanResidual = lumaResidual / Math.max(1, residualCount);
        const meanChromaResidual = chromaResidual / Math.max(1, residualCount);
        const lumaNoiseScore = clamp01((meanResidual - 2.5) / 24);
        const chromaNoiseScore = clamp01((meanChromaResidual - 2.5) / 22);
        const noiseScore = Math.max(lumaNoiseScore, chromaNoiseScore);
        const moireScore = clamp01((alternationCount / Math.max(1, alternationSamples) - 0.03) * 4);
        const oversharpeningScore = clamp01(haloCount / Math.max(1, edgeCount) * 2.5);
        const edgeDensity = edgeCount / total;
        const motionBlurScore = edgeDensity < 0.008 ? 0 : clamp01(1 - strongEdgeCount / Math.max(1, edgeCount) * 1.25);
        const adjacentMean = adjacentRowDifference / Math.max(1, adjacentRowCount);
        const parityMean = parityRowDifference / Math.max(1, parityRowCount);
        const interlacingScore = clamp01((adjacentMean - parityMean - 2) / 24);
        let flickerScore = 0;
        let duplicatedFrame = false;
        let droppedFrame = false;
        let rollingShutterScore = 0;
        if (previous && previous.width === width && previous.height === height) {
            let signedDifference = 0;
            let absoluteDifference = 0;
            let squaredDifference = 0;
            for (let index = 0; index < luma.length; index++) {
                const difference = (luma[index] ?? 0) - (previous.luma[index] ?? 0);
                signedDifference += difference;
                absoluteDifference += Math.abs(difference);
                squaredDifference += difference * difference;
            }
            const meanSigned = signedDifference / total;
            const meanAbsolute = absoluteDifference / total;
            const differenceDeviation = Math.sqrt(Math.max(0, squaredDifference / total - meanSigned * meanSigned));
            flickerScore = clamp01(Math.abs(meanSigned) / 35) * clamp01(1 - differenceDeviation / 25);
            duplicatedFrame = meanAbsolute < 0.35;
            rollingShutterScore = this.rollingShutterRisk(luma, previous.luma, width, height, meanAbsolute);
        }
        if (intervalMs !== undefined && intervalMs > 0 && this.previousIntervalMs !== undefined) {
            droppedFrame = intervalMs > this.previousIntervalMs * 1.8;
        }
        const metrics = {
            macroblocking_score: round(macroblockingScore), banding_score: round(bandingScore),
            noise_score: round(noiseScore), flicker_score: round(flickerScore), moire_score: round(moireScore),
            oversharpening_score: round(oversharpeningScore), motion_blur_score: round(motionBlurScore),
            interlacing_score: round(interlacingScore), dead_pixel_count: deadPixels.length,
        };
        const detected = [];
        const addArtifact = (type, score, affectedRegion, description) => {
            detected.push({ type, severity: severity(score), confidence: round(clamp01(0.45 + score * 0.5)), affected_region: affectedRegion, description });
        };
        if (macroblockingScore >= 0.18)
            addArtifact('compression', macroblockingScore, 'full_frame', 'Block-boundary discontinuities suggest compression or macroblocking.');
        if (bandingScore >= 0.25)
            addArtifact('banding', bandingScore, 'background', 'Quantized luminance steps suggest visible gradient banding.');
        if (noiseScore >= 0.2)
            addArtifact('noise', noiseScore, meanChromaResidual > meanResidual ? 'full_frame' : 'shadows', 'High-frequency luma or chroma variation suggests image noise.');
        if (flickerScore >= 0.2)
            addArtifact('flicker', flickerScore, 'full_frame', 'Frame-wide luminance changed without equivalent structural change.');
        if (moireScore >= 0.25)
            addArtifact('moire', moireScore, 'full_frame', 'Alternating high-frequency detail suggests moiré or aliasing.');
        if (oversharpeningScore >= 0.2)
            addArtifact('oversharpening', oversharpeningScore, 'edges', 'Local edge overshoot suggests sharpening halos.');
        if (motionBlurScore >= 0.4)
            addArtifact('motion_blur', motionBlurScore, 'edges', 'Edge gradients appear broadly softened.');
        if (duplicatedFrame)
            addArtifact('duplicated_frame', 0.8, 'full_frame', 'The frame is effectively identical to the previous frame.');
        if (droppedFrame)
            addArtifact('dropped_frame', 0.8, 'full_frame', 'The frame interval is substantially longer than the recent cadence.');
        if (interlacingScore >= 0.22)
            addArtifact('interlacing', interlacingScore, 'edges', 'Alternating scanline differences suggest interlacing combing.');
        if (deadPixels.length)
            addArtifact('dead_pixel', clamp01(deadPixels.length / 8), 'full_frame', `${deadPixels.length} isolated pixel outlier(s) detected.`);
        if (rollingShutterScore >= 0.35)
            addArtifact('rolling_shutter', rollingShutterScore, 'edges', 'Row-dependent edge displacement suggests possible rolling shutter skew.');
        const warnings = [];
        const warn = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (macroblockingScore >= 0.18)
            warn('compression_artifacts', 'Compression artifacts may damage key edges');
        if (bandingScore >= 0.25)
            warn('background_banding', 'Banding detected in background gradient');
        if (flickerScore >= 0.2)
            warn('temporal_flicker', 'Temporal flicker detected');
        if (chromaNoiseScore >= 0.22)
            warn('chroma_noise', 'High chroma noise detected');
        if (motionBlurScore >= 0.4)
            warn('motion_blur', 'Motion blur may reduce tracking accuracy');
        if (duplicatedFrame || droppedFrame)
            warn('frame_cadence', 'Possible dropped or duplicated frame');
        if (oversharpeningScore >= 0.2)
            warn('oversharpening_halos', 'Over-sharpening halos detected around edges');
        if (interlacingScore >= 0.22)
            warn('interlacing_combing', 'Interlacing combing detected');
        const overlays = [];
        artifactCellCounts.forEach((count, index) => {
            if (!count || (artifactCells[index] ?? 0) / count < 14)
                return;
            const column = index % 12;
            const row = Math.floor(index / 12);
            overlays.push({ type: 'heatmap', points: [{ x: column / 12, y: row / 8 }, { x: (column + 1) / 12, y: (row + 1) / 8 }], label: 'Artifact heatmap', color: '#ff6b6b', opacity: 0.45, stroke_width: 1, fill: true });
        });
        if (macroblockingScore >= 0.18 || bandingScore >= 0.25 || interlacingScore >= 0.22) {
            overlays.push({ type: 'box', points: [{ x: 0, y: 0 }, { x: 1, y: 1 }], label: 'Artifact affected region', color: '#ff9f43', opacity: 0.55, stroke_width: 2 });
        }
        deadPixels.slice(0, 32).forEach((pixel) => overlays.push({ type: 'point', points: [{ x: pixel.x / width, y: pixel.y / height }], label: 'Dead/stuck pixel candidate', color: '#ff3366', opacity: 1, stroke_width: 3 }));
        const temporalScore = Math.max(flickerScore, duplicatedFrame || droppedFrame ? 0.8 : 0, rollingShutterScore);
        const scores = [macroblockingScore, bandingScore, noiseScore, moireScore, oversharpeningScore, motionBlurScore, interlacingScore, temporalScore, clamp01(deadPixels.length / 8)];
        const artifactScore = clamp01(Math.max(...scores) * 0.75 + scores.reduce((sum, score) => sum + score, 0) / scores.length * 0.25);
        const risk = artifactScore >= 0.55 ? 'high' : artifactScore >= 0.25 ? 'medium' : 'low';
        const result = {
            module_id: this.id, module_name: this.label, confidence: round(clamp01(0.68 + total / 500_000)),
            frame_number: context.frameNumber, timestamp_ms: context.timestampMs,
            artifact_score: round(artifactScore), overall_artifact_risk: risk,
            detected_artifacts: detected, metrics, warnings, overlays,
        };
        this.lastResult = result;
        this.previous = features;
        if (intervalMs !== undefined && intervalMs > 0)
            this.previousIntervalMs = intervalMs;
        return result;
    }
    extractFeatures(frame, timestampMs) {
        const stride = Math.max(1, Math.ceil(Math.sqrt((frame.width * frame.height) / MAX_SAMPLES)));
        const width = Math.ceil(frame.width / stride);
        const height = Math.ceil(frame.height / stride);
        const luma = new Float32Array(width * height);
        const cb = new Float32Array(width * height);
        const cr = new Float32Array(width * height);
        let sum = 0;
        let sampleY = 0;
        for (let y = 0; y < frame.height; y += stride) {
            let sampleX = 0;
            for (let x = 0; x < frame.width; x += stride) {
                const source = (y * frame.width + x) * 4;
                const target = sampleY * width + sampleX;
                const r = frame.data[source] ?? 0;
                const g = frame.data[source + 1] ?? 0;
                const b = frame.data[source + 2] ?? 0;
                const value = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                luma[target] = value;
                cb[target] = b - value;
                cr[target] = r - value;
                sum += value;
                sampleX++;
            }
            sampleY++;
        }
        return { width, height, luma, cb, cr, meanLuma: sum / Math.max(1, width * height), timestampMs };
    }
    rollingShutterRisk(current, previous, width, height, temporalDifference) {
        if (temporalDifference < 3 || width < 8 || height < 8)
            return 0;
        const positions = [];
        for (let y = 0; y < height; y += Math.max(1, Math.floor(height / 24))) {
            let strongest = 0;
            let position = 0;
            for (let x = 1; x < width; x++) {
                const gradient = Math.abs((current[y * width + x] ?? 0) - (current[y * width + x - 1] ?? 0));
                if (gradient > strongest) {
                    strongest = gradient;
                    position = x;
                }
            }
            if (strongest > 35)
                positions.push(position);
        }
        if (positions.length < 6)
            return 0;
        const range = Math.max(...positions) - Math.min(...positions);
        return clamp01((range / width - 0.05) * 4);
    }
}
//# sourceMappingURL=VideoArtifactsAnalyzer.js.map